import { fg } from '@atlaskit/platform-feature-flags';
import type { SkillSelectionSource } from '@atlassian/conversation-assistant-instrumentation/types/analytics-properties-context';
import { type StreamResponse } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { constructCitedProductList } from './constructCitedProductList';
import { constructSourceProducts } from './constructSourceProducts';
import { containsServiceDeskSource } from './containsServiceDeskSource';
import { getInvokedFrom } from './getInvokedFrom';
import type { SkillAttributes } from './SkillAttributes';
import type { StreamMeta } from './StreamMeta';

export const extractStreamMeta = (
	response: StreamResponse,
	skillSelectionSource?: SkillSelectionSource,
	/**
	 * Whether to include messageDynamicUIData (action subtype metadata).
	 * Pass the result of expValEquals('cc-maui-experiment', 'isEnabled', true) from the call site
	 * to keep experiment checks out of this utility function.
	 */
	includeDynamicUIData?: boolean,
): StreamMeta => {
	const sources = response.message?.message?.sources;
	const pluginInvocations = response.message?.message?.plugin_invocations;
	const minionOutputs = response.message?.message?.minion_outputs;
	const actions = response.message?.message?.actions;
	const humanMessageId = response.message?.human_message?.id.toString();
	const pluginsInvoked: string[] = [];
	const skillsInvoked: SkillAttributes[] = [];
	let usesJsm = false;

	pluginInvocations?.forEach((pi) => {
		if (pi.plugin_name) {
			pluginsInvoked.push(pi.plugin_name);
		}
		if (
			pi.plugin_output &&
			containsServiceDeskSource(pi.plugin_output) &&
			fg('rovo_chat_analytics_source_extended')
		) {
			usesJsm = true;
		}
	});

	const flatMinionOutputs = minionOutputs?.flat() ?? [];

	const executedStratusSkill =
		flatMinionOutputs.some((output) => output.skill_type === 'STRATUS') ?? false;

	flatMinionOutputs.forEach((output) => {
		pluginsInvoked.push(output.minion_id);
		const isExplicit = output.directly_invoked;
		skillsInvoked.push({
			minionId: output.minion_id,
			skillName: output.skill_alias,
			skillType: output.skill_type,
			invocationType: isExplicit ? 'explicit' : 'implicit',
			internalTools:
				output.internal_tools?.map((tool) => ({
					name: tool.name,
					isConsequential: tool.is_consequential,
				})) ?? [],
			skillSelectionSource: isExplicit ? skillSelectionSource : undefined,
		});

		if (
			output.minion_output &&
			containsServiceDeskSource(output.minion_output) &&
			fg('rovo_chat_analytics_source_extended')
		) {
			usesJsm = true;
		}
	});

	return {
		humanMessageId,
		citedProductList: constructCitedProductList(sources),
		sourceProducts: constructSourceProducts(sources),
		hasSources: !!sources?.length,
		pluginsInvoked,
		skillsInvoked,
		executedStratusSkill,
		messageActions: actions?.map((action) => action.key),
		messageDynamicUIData: includeDynamicUIData
			? actions?.map((action) => ({
					subtype: (action.data as { dynamicUiSubtype?: string } | undefined)?.dynamicUiSubtype,
				}))
			: undefined,
		invokedFrom: getInvokedFrom({
			messageActionKey: actions?.[0]?.key,
			messageActionData: actions?.[0]?.data,
		}),
		responseSources: fg('rovo_chat_analytics_source_extended')
			? sources?.map((x) => x.ari).filter(Boolean)
			: undefined,
		usesJsm: fg('rovo_chat_analytics_source_extended') ? usesJsm : undefined,
		scenarioId: response.message?.message?.scenario?.id,
		interactionModeExecuted: response.message?.message?.message_metadata?.interaction_mode_executed,
	};
};
